import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import {
  BookOpen,
  Command,
  ExternalLink,
  GraduationCap,
  Lock,
  PencilLine,
  Route,
} from 'lucide-react'
import { ChromeIcon } from '@/components/ChromeIcon'
import { QueueIcon, type QueueIconColor, type QueueIconMeaning } from '@/components/QueueIcon'
import { useModalA11y } from '@/hooks/useModalA11y'
import { SHORTCUT_GROUPS, spokenKeys } from '@/components/command/shortcuts'
import { useOptionalFirstRun } from '@/components/first-run/FirstRunProvider'
import { HelpSetupSection } from '@/components/first-run/SetupChecklist'
import {
  HELP_CENTER_URL,
  helpArticle,
  helpArticleUrl,
  searchHelpArticles,
  type HelpArticle,
} from './articleIndex'
import { HELP_INDEX, type HelpIntent } from './helpIntent'
import { ThisPageCard, TutorialView, findTutorial } from './HelpTutorials'
import { GETTING_STARTED_SLUGS, suggestionsForRoute } from './suggestions'
import styles from './help.module.css'

/** Each help category wears the Queue's clay for the closest meaning. */
const CATEGORY_CLAY: Record<string, { meaning: QueueIconMeaning; color: QueueIconColor }> = {
  'getting-started': { meaning: 'account', color: 'coral' },
  'your-number': { meaning: 'call', color: 'green' },
  messaging: { meaning: 'text', color: 'lavender' },
  'invoices-payments': { meaning: 'invoice', color: 'blue' },
  'billing-plans': { meaning: 'update', color: 'yellow' },
  'account-settings': { meaning: 'notification', color: 'navy' },
}
const DEFAULT_CLAY = CATEGORY_CLAY['getting-started']

/** Route suggestions first, then the getting-started articles, four at most. */
const MAX_SUGGESTIONS = 4

/** The shell header's Help button: where focus returns when the opener is gone. */
const helpTrigger = () => document.querySelector<HTMLElement>('[data-help-trigger]')

function ArticleCard({ article }: { article: HelpArticle }) {
  const clay = CATEGORY_CLAY[article.category] ?? DEFAULT_CLAY
  return (
    <a
      className={styles.articleCard}
      href={helpArticleUrl(article)}
      target="_blank"
      rel="noreferrer"
    >
      <QueueIcon meaning={clay.meaning} color={clay.color} className={styles.articleClay} />
      <span className={styles.articleText}>
        <span className={styles.articleTitle}>{article.title}</span>
        <span className={styles.articleDesc}>{article.description}</span>
      </span>
      <ExternalLink size={13} aria-hidden className={styles.articleExt} />
      <span className={styles.srOnly}> (opens the help center in a new tab)</span>
    </a>
  )
}

/** Help → Shortcuts: the shell's real keyboard shortcuts (shortcuts.ts),
 *  labelled with this platform's modifier. */
function ShortcutsView() {
  return (
    <article className={styles.shortcuts} aria-labelledby="help-shortcuts-title">
      <h3 id="help-shortcuts-title" className={styles.tutorialTitle}>
        Keyboard shortcuts
      </h3>
      {SHORTCUT_GROUPS.map((group) => (
        <section key={group.label} className={styles.section}>
          <h4 className={styles.sectionLabel}>{group.label}</h4>
          <dl className={styles.shortcutList}>
            {group.shortcuts.map((shortcut) => (
              <div key={shortcut.label} className={styles.shortcutRow}>
                <dt>{shortcut.label}</dt>
                <dd>
                  <span className={styles.srOnly}>{spokenKeys(shortcut)}</span>
                  <span className={styles.shortcutKeys} aria-hidden="true">
                    {shortcut.keys.map((key) => (
                      <kbd key={key} className={styles.shortcutKey}>
                        {key}
                      </kbd>
                    ))}
                  </span>
                </dd>
              </div>
            ))}
          </dl>
          {group.note && <p className={styles.shortcutNote}>{group.note}</p>}
        </section>
      ))}
    </article>
  )
}

type FocusTarget =
  | { to: 'back' }
  | { to: 'search' }
  | { to: 'shortcuts' }
  | { to: 'card'; tutorialId: string }

interface HelpDrawerProps {
  /** What to show first; a new intent while open switches to it. */
  intent?: HelpIntent
  /** True while the drawer plays its exit: inert, focus already returned. */
  closing?: boolean
  onClose: () => void
  onOpenMessenger: () => void
}

/**
 * The Help drawer (2026-09-23 makeover of the Attio-referenced drawer in
 * plans/help-support-status.md). The index reads, in order: Get set up (the
 * owner's setup steps, moved here from Home), This page (the current page's
 * short explanation video), Suggestions (help-center articles). The tutorial
 * view plays a page's video with its written explanation beneath. Every view
 * ends with a quiet More list (Replay the tour, Reset tutorial progress, Terms
 * and privacy) above one pinned footer row: Help center, Support, Shortcuts
 * (review fixes 2026-09-24, so This page stays above the fold). Shortcuts
 * swaps in the keyboard shortcuts list the way a tutorial does. `intent` picks
 * the first view; Help always reopens on the index unless a caller asks for a
 * video. Closing slides the drawer back out (HelpSurface keeps it mounted).
 */
export function HelpDrawer({ intent = HELP_INDEX, closing = false, onClose, onOpenMessenger }: HelpDrawerProps) {
  const { pathname } = useLocation()
  const firstRun = useOptionalFirstRun()
  const [query, setQuery] = useState('')
  const [view, setView] = useState<HelpIntent>(intent)
  // Opened from the peek tab, the opener retires once the video counts as
  // watched, so closing falls back to the header's Help button.
  const dialogRef = useModalA11y<HTMLElement>(!closing, onClose, { returnFocus: helpTrigger })
  const bodyRef = useRef<HTMLDivElement | null>(null)
  const backRef = useRef<HTMLButtonElement | null>(null)
  const searchRef = useRef<HTMLInputElement | null>(null)
  const shortcutsRef = useRef<HTMLButtonElement | null>(null)
  // Where keyboard focus goes after a view switch. Null leaves it alone — the
  // first open wants that, since useModalA11y places focus and remembers the
  // opener to hand focus back to.
  const focusAfterSwitch = useRef<FocusTarget | null>(null)
  const mounted = useRef(false)

  // A new request while open (Get set up's Watch, Reset tutorial progress)
  // replaces whatever the drawer was showing.
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true
      return
    }
    // An index request while open comes from Reset tutorial progress at the end of the
    // body; the view scrolls to the top, so focus goes to the top with it.
    focusAfterSwitch.current = intent.view === 'index' ? { to: 'search' } : { to: 'back' }
    setView(intent)
    setQuery('')
  }, [intent])

  const tutorial = view.view === 'tutorial' ? findTutorial(view.tutorialId) : undefined
  const shortcuts = view.view === 'shortcuts'

  const openTutorial = useCallback((tutorialId: string) => {
    focusAfterSwitch.current = { to: 'back' }
    setView({ view: 'tutorial', tutorialId })
  }, [])
  const openShortcuts = useCallback(() => {
    focusAfterSwitch.current = { to: 'back' }
    setView({ view: 'shortcuts' })
  }, [])
  const backToIndex = useCallback(() => {
    focusAfterSwitch.current =
      view.view === 'tutorial'
        ? { to: 'card', tutorialId: view.tutorialId }
        : view.view === 'shortcuts'
          ? { to: 'shortcuts' }
          : null
    setView(HELP_INDEX)
  }, [view])

  // Each view starts at the top; keyboard focus follows the switch.
  useLayoutEffect(() => {
    bodyRef.current?.scrollTo({ top: 0 })
    const target = focusAfterSwitch.current
    focusAfterSwitch.current = null
    if (!target) return
    if (target.to === 'back') {
      backRef.current?.focus()
    } else if (target.to === 'search') {
      searchRef.current?.focus()
    } else if (target.to === 'shortcuts') {
      shortcutsRef.current?.focus()
    } else {
      const card = dialogRef.current?.querySelector<HTMLElement>(`[data-help-tutorial="${target.tutorialId}"]`)
      ;(card ?? searchRef.current)?.focus()
    }
  }, [view, dialogRef])

  const suggestions = useMemo(() => {
    const slugs = [...new Set([...suggestionsForRoute(pathname), ...GETTING_STARTED_SLUGS])]
    return slugs
      .map(helpArticle)
      .filter((a): a is HelpArticle => !!a)
      .slice(0, MAX_SUGGESTIONS)
  }, [pathname])
  const results = useMemo(() => searchHelpArticles(query), [query])
  const searching = query.trim().length > 0

  // The only way back into the first-run tour once it has been seen
  // (design system Components · 09: never re-shown, always replayable here).
  const replayTour = () => {
    onClose()
    firstRun?.replayTour()
  }
  // Clears the tour, the sample and every watched video. FirstRunProvider
  // then asks the layout for Help's index, so the drawer stays open and lands
  // on Get set up.
  const resetTutorial = () => {
    firstRun?.resetTutorial()
  }

  return createPortal(
    <div
      className={closing ? `${styles.drawerRoot} ${styles.drawerClosing}` : styles.drawerRoot}
      {...(closing ? { inert: '' as const } : {})}
    >
      <button
        type="button"
        className={styles.drawerScrim}
        aria-label="Close help"
        onClick={onClose}
        tabIndex={-1}
      />
      <aside
        ref={dialogRef}
        className={styles.drawer}
        role="dialog"
        aria-modal="true"
        aria-label="Help"
        tabIndex={-1}
      >
        <header className={styles.drawerHeader}>
          {tutorial || shortcuts ? (
            <button
              ref={backRef}
              type="button"
              className={styles.backBtn}
              onClick={backToIndex}
              aria-label="Back to Help"
            >
              <ChromeIcon name="back" size={16} tone="muted" />
              <span className={styles.drawerTitle}>Help</span>
            </button>
          ) : (
            <div className={styles.drawerHeading}>
              <ChromeIcon name="help" size={18} />
              <h2 className={styles.drawerTitle}>Help</h2>
            </div>
          )}
          <button type="button" className={styles.iconBtn} aria-label="Close help" onClick={onClose}>
            <ChromeIcon name="close" size={18} />
            <span className={styles.iconTip} role="tooltip">
              Close help
            </span>
          </button>
        </header>

        {!tutorial && !shortcuts && (
          <div className={styles.searchWrap}>
            <ChromeIcon name="search" size={15} tone="muted" className={styles.searchIcon} />
            <input
              ref={searchRef}
              type="search"
              className={styles.searchInput}
              placeholder="Search help"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search help articles"
            />
          </div>
        )}

        <div ref={bodyRef} className={styles.drawerBody}>
          {tutorial ? (
            <TutorialView
              key={tutorial.id}
              tutorial={tutorial}
              onTakeTour={
                firstRun && tutorial.id === 'home'
                  ? () => {
                      onClose()
                      firstRun.replayTour()
                    }
                  : undefined
              }
            />
          ) : shortcuts ? (
            <ShortcutsView />
          ) : searching ? (
            <section aria-label="Search results" className={styles.section}>
              {results.length > 0 ? (
                <div className={styles.articleList}>
                  {results.map((a) => <ArticleCard key={a.slug} article={a} />)}
                </div>
              ) : (
                <div className={styles.searchEmpty}>
                  <p>No articles match “{query.trim()}”.</p>
                  <a href={HELP_CENTER_URL} target="_blank" rel="noreferrer">
                    Browse the help center <ExternalLink size={13} aria-hidden />
                  </a>
                </div>
              )}
            </section>
          ) : (
            <>
              {firstRun && <HelpSetupSection onLeave={onClose} />}
              <ThisPageCard pathname={pathname} onOpen={openTutorial} />
              <section aria-labelledby="help-suggestions-title" className={styles.section}>
                <h3 id="help-suggestions-title" className={styles.sectionLabel}>
                  Suggestions
                </h3>
                <div className={styles.articleList}>
                  {suggestions.map((a) => (
                    <ArticleCard key={a.slug} article={a} />
                  ))}
                </div>
              </section>
            </>
          )}

          {/* The rarer links end every view, so the pinned footer stays one row. */}
          <section aria-labelledby="help-more-title" className={styles.section}>
            <h3 id="help-more-title" className={`${styles.sectionLabel} ${styles.moreLabel}`}>
              More
            </h3>
            <ul className={styles.moreList}>
              {firstRun && (
                <>
                  <li>
                    <button type="button" className={styles.footerLink} onClick={replayTour}>
                      <Route size={15} aria-hidden />
                      <span>Replay the tour</span>
                    </button>
                  </li>
                  <li>
                    <button type="button" className={styles.footerLink} onClick={resetTutorial}>
                      <GraduationCap size={15} aria-hidden />
                      <span>Reset tutorial progress</span>
                    </button>
                  </li>
                </>
              )}
              <li>
                <a className={styles.footerLink} href="/terms" target="_blank" rel="noreferrer">
                  <Lock size={15} aria-hidden />
                  <span>Terms and privacy</span>
                </a>
              </li>
            </ul>
          </section>
        </div>

        <footer className={styles.drawerFooter}>
          <a className={styles.footerLink} href={HELP_CENTER_URL} target="_blank" rel="noreferrer">
            <BookOpen size={15} aria-hidden />
            <span>Help center</span>
            <ExternalLink size={12} aria-hidden className={styles.footerExt} />
          </a>
          <button type="button" className={styles.footerLink} onClick={onOpenMessenger}>
            <PencilLine size={15} aria-hidden />
            <span>Support</span>
          </button>
          <button ref={shortcutsRef} type="button" className={styles.footerLink} onClick={openShortcuts}>
            <Command size={15} aria-hidden />
            <span>Shortcuts</span>
          </button>
        </footer>
      </aside>
    </div>,
    document.body,
  )
}
